/**
 * BTTC Bridge API Service
 *
 * BitTorrent Chain (BTTC) is TRON's native cross-chain bridge
 * supporting transfers between TRON, Ethereum, and BSC.
 *
 * Bridge process:
 * 1. Deposit tokens on source chain (locks tokens)
 * 2. Wait for bridge validators (~15-30 min)
 * 3. Claim/withdraw on destination chain
 *
 * Docs: https://doc.bt.io/docs/bridge/overview
 *
 * All contract addresses and token addresses switch via networkConfig.
 */

import { getBttcConfig } from './networkConfig';

// Dynamic BTTC contract lookup reflecting current network
function getBttcContracts() {
  return getBttcConfig().contracts;
}

export const BTTC_CONTRACTS: {
  tron: { rootChainManager: string; erc20Predicate: string };
  bttc: { childChainManager: string; stateReceiver: string };
  ethereum: { rootChainManager: string; erc20Predicate: string };
  bsc: { rootChainManager: string; erc20Predicate: string };
} = new Proxy({} as any, {
  get(_target, prop: string) {
    return (getBttcContracts() as any)[prop];
  },
  ownKeys() {
    return Object.keys(getBttcContracts());
  },
  getOwnPropertyDescriptor(_target, prop: string) {
    const contracts = getBttcContracts() as any;
    if (prop in contracts) {
      return { configurable: true, enumerable: true, value: contracts[prop] };
    }
    return undefined;
  },
});

// Dynamic bridgeable token lookup reflecting current network
export const BRIDGEABLE_TOKENS: Record<string, {
  symbol: string;
  decimals: number;
  tron?: string;
  bttc?: string;
  ethereum?: string;
  bsc?: string;
}> = new Proxy({} as any, {
  get(_target, prop: string) {
    return getBttcConfig().bridgeableTokens[prop];
  },
  ownKeys() {
    return Object.keys(getBttcConfig().bridgeableTokens);
  },
  getOwnPropertyDescriptor(_target, prop: string) {
    const tokens = getBttcConfig().bridgeableTokens;
    if (prop in tokens) {
      return { configurable: true, enumerable: true, value: tokens[prop] };
    }
    return undefined;
  },
  has(_target, prop: string) {
    return prop in getBttcConfig().bridgeableTokens;
  },
});

export type BridgeChain = 'tron' | 'bttc' | 'ethereum' | 'bsc';

export interface BridgeQuoteRequest {
  fromChain: BridgeChain;
  toChain: BridgeChain;
  tokenSymbol: string;
  amount: string; // Human readable
}

export interface BridgeQuote {
  fromChain: BridgeChain;
  toChain: BridgeChain;
  tokenSymbol: string;
  amountIn: string;
  amountOut: string; // 1:1 bridge, minus fees
  estimatedFee: string;
  estimatedTime: number; // seconds
  route: string[];
  isSupported: boolean;
  error?: string;
}

export interface BridgeTransactionParams {
  fromChain: BridgeChain;
  toChain: BridgeChain;
  tokenSymbol: string;
  amount: string;
  recipient: string;
}

/**
 * Map chain name to BridgeChain type
 */
export function mapChainToBridgeChain(chainName: string): BridgeChain | null {
  const mapping: Record<string, BridgeChain> = {
    'tron': 'tron',
    'bttc': 'bttc',
    'ethereum': 'ethereum',
    'eth': 'ethereum',
    'bsc': 'bsc',
    'bnb chain': 'bsc',
    'binance': 'bsc',
  };
  return mapping[chainName.toLowerCase()] || null;
}

/**
 * Check if a token is bridgeable between two chains
 */
export function isBridgeable(tokenSymbol: string, fromChain: BridgeChain, toChain: BridgeChain): boolean {
  const token = BRIDGEABLE_TOKENS[tokenSymbol.toUpperCase()];
  if (!token) return false;

  // Check if token exists on both chains
  const fromKey = fromChain as keyof typeof token;
  const toKey = toChain as keyof typeof token;

  return !!(token[fromKey] && token[toKey]);
}

class BTTCBridgeService {
  /**
   * Get a bridge quote
   * BTTC bridge is 1:1, fees are gas costs only
   */
  async getQuote(request: BridgeQuoteRequest): Promise<BridgeQuote> {
    const token = BRIDGEABLE_TOKENS[request.tokenSymbol.toUpperCase()];

    if (!token) {
      return {
        ...request,
        amountIn: request.amount,
        amountOut: '0',
        estimatedFee: '0',
        estimatedTime: 0,
        route: [],
        isSupported: false,
        error: `Token ${request.tokenSymbol} is not supported by BTTC bridge`,
      };
    }

    const fromKey = request.fromChain as keyof typeof token;
    const toKey = request.toChain as keyof typeof token;

    if (!token[fromKey] || !token[toKey]) {
      return {
        ...request,
        amountIn: request.amount,
        amountOut: '0',
        estimatedFee: '0',
        estimatedTime: 0,
        route: [],
        isSupported: false,
        error: `${request.tokenSymbol} bridge not available between ${request.fromChain} and ${request.toChain}`,
      };
    }

    // BTTC bridge is 1:1, only gas fees apply
    // Estimate gas fees based on chains involved
    let estimatedFee = '0';
    let estimatedTime = 900; // 15 minutes default

    if (request.fromChain === 'tron') {
      estimatedFee = '~50 TRX'; // TRON deposit gas
      estimatedTime = 420; // ~7 minutes for TRON->BTTC
    } else if (request.fromChain === 'ethereum') {
      estimatedFee = '~$5-20'; // ETH gas varies
      estimatedTime = 900; // ~15 minutes
    } else if (request.fromChain === 'bsc') {
      estimatedFee = '~$0.50'; // BSC gas is cheap
      estimatedTime = 600; // ~10 minutes
    }

    // Build route description
    const route: string[] = [];
    if (request.fromChain !== 'bttc' && request.toChain !== 'bttc') {
      // Cross-chain via BTTC
      route.push(`${request.fromChain.toUpperCase()} → BTTC`);
      route.push(`BTTC → ${request.toChain.toUpperCase()}`);
      estimatedTime *= 2; // Two bridge operations
    } else {
      route.push(`${request.fromChain.toUpperCase()} → ${request.toChain.toUpperCase()}`);
    }

    return {
      fromChain: request.fromChain,
      toChain: request.toChain,
      tokenSymbol: request.tokenSymbol,
      amountIn: request.amount,
      amountOut: request.amount, // 1:1 bridge
      estimatedFee,
      estimatedTime,
      route,
      isSupported: true,
    };
  }

  /**
   * Get supported tokens for bridging
   */
  getSupportedTokens(): string[] {
    return Object.keys(BRIDGEABLE_TOKENS);
  }

  /**
   * Get token address on a specific chain
   */
  getTokenAddress(symbol: string, chain: BridgeChain): string | null {
    const token = BRIDGEABLE_TOKENS[symbol.toUpperCase()];
    if (!token) return null;
    return token[chain as keyof typeof token] as string || null;
  }

  /**
   * Build deposit transaction for TRON -> BTTC
   * Returns transaction parameters for TronWeb
   */
  async buildTronDepositTransaction(params: BridgeTransactionParams): Promise<{
    contractAddress: string;
    functionSelector: string;
    parameter: string;
    callValue: number;
  }> {
    const tokenAddress = this.getTokenAddress(params.tokenSymbol, 'tron');
    if (!tokenAddress) {
      throw new Error(`Token ${params.tokenSymbol} not supported for TRON bridge`);
    }

    // First: Approve ERC20Predicate to spend tokens
    // Then: Call depositFor on RootChainManager

    // For simplicity, return the depositFor call
    // In practice, you'd need to check allowance and approve first
    return {
      contractAddress: BTTC_CONTRACTS.tron.rootChainManager,
      functionSelector: 'depositFor(address,address,uint256)',
      parameter: '', // Would need ABI encoding
      callValue: 0,
    };
  }

  /**
   * Get bridge status - placeholder for tracking
   */
  async getBridgeStatus(_txHash: string): Promise<{
    status: 'pending' | 'deposited' | 'checkpointed' | 'completed' | 'failed';
    message: string;
  }> {
    // Would need to query both chains and checkpoints
    return {
      status: 'pending',
      message: 'Bridge status tracking requires indexer integration',
    };
  }
}

export const bttcBridgeApi = new BTTCBridgeService();
export { BTTCBridgeService };
